import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('admin_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Cette page utilise sa propre instance axios : sans cet intercepteur, un jeton
// expiré renvoyait un 401 que personne ne traitait, et la page restait vide.
api.interceptors.response.use(r => r, err => {
  if (err.response?.status === 401) {
    localStorage.removeItem('admin_token');
    window.location.href = '/login';
  }
  return Promise.reject(err);
});

// Périmètre courant — visite technique ou production. Covage suit les deux
// séparément avec les mêmes indicateurs. Plutôt que de le passer à travers la
// dizaine de composants qui interrogent l'API, on l'injecte ici : aucun panneau
// n'a à s'en souvenir, et aucun ne peut l'oublier.
let _perimetre = 'production';
export function perimetreCourant() { return _perimetre; }
export function definirPerimetre(p) { _perimetre = p; }
api.interceptors.request.use(cfg => {
  if (cfg.url && cfg.url.startsWith('/admin/kpi')) {
    cfg.params = { ...(cfg.params || {}), perimetre: _perimetre };
  }
  return cfg;
});

// Remontée des erreurs de chargement vers la bannière de la page.
let _signalerErreur = null;
export function abonnerErreurKpi(fn) { _signalerErreur = fn; }
function signalerErreur(e) {
  if (!_signalerErreur) return;
  const s = e?.response?.status;
  const detail = e?.response?.data?.error;
  if (s === 403) _signalerErreur('Accès refusé : votre compte n\'a pas les droits pour consulter les KPI. Reconnectez-vous ou contactez l\'administrateur.');
  else if (s) _signalerErreur(`Erreur de chargement (HTTP ${s})${detail ? ' — ' + detail : ''}`);
  else _signalerErreur('Impossible de joindre le serveur. Vérifiez votre connexion puis rafraîchissez la page.');
}

const MOIS_LONG = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
function monthLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return `${MOIS_LONG[parseInt(m, 10) - 1]} ${y}`;
}

// ── Graphique à barres groupées ───────────────────────────────────────────────
function BarChart({ series, objective = 90 }) {
  if (!series?.length) return <div style={{ color: 'var(--text3)', fontSize: 13, padding: 20 }}>Aucune donnée</div>;

  const maxTotal = Math.max(...series.map(s => s.total), 1);

  return (
    <div style={{ position: 'relative' }}>
      {/* Ligne objectif */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 28, pointerEvents: 'none' }}>
        <div style={{
          position: 'absolute',
          top: `${100 - objective}%`,
          left: 0, right: 0,
          borderTop: '2px dashed #DC2626',
          zIndex: 2,
        }}>
          <span style={{ position: 'absolute', right: 0, top: -16, fontSize: 10, color: '#DC2626', fontWeight: 700, background: 'var(--bg)', padding: '1px 4px' }}>
            Objectif {objective}%
          </span>
        </div>
      </div>

      {/* Barres */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 200, paddingBottom: 28, position: 'relative' }}>
        {series.map((s, i) => {
          const h = (n) => Math.max((n / maxTotal) * 160, n > 0 ? 3 : 0);
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
              {/* Taux label */}
              <div style={{ fontSize: 10, fontWeight: 700, color: s.taux_lt2 >= objective ? '#059669' : '#DC2626', marginBottom: 3 }}>
                {s.taux_lt2}%
              </div>
              {/* Barres empilées */}
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column-reverse', gap: 1 }}>
                <div style={{ width: '100%', height: h(s.lt2), background: '#2563EB', borderRadius: '2px 2px 0 0', transition: 'height .4s' }} title={`<2J: ${s.lt2}`} />
                <div style={{ width: '100%', height: h(s.gte2), background: '#F59E0B', transition: 'height .4s' }} title={`2-5J: ${s.gte2}`} />
                <div style={{ width: '100%', height: h(s.gt5), background: '#DC2626', borderRadius: '2px 2px 0 0', transition: 'height .4s' }} title={`>5J: ${s.gt5}`} />
              </div>
              {/* Total */}
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{s.total}</div>
              {/* Mois label */}
              <div style={{
                position: 'absolute', bottom: 0,
                fontSize: 10, color: 'var(--text3)', fontWeight: 600,
                left: `${(i / series.length) * 100}%`,
                width: `${100 / series.length}%`,
                textAlign: 'center',
                whiteSpace: 'nowrap',
              }}>{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Légende */}
      <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11, flexWrap: 'wrap' }}>
        {[
          { color: '#2563EB', label: 'Planifiés <2J ouvrés' },
          { color: '#F59E0B', label: 'Planifiés >2 à 5J ouvrés' },
          { color: '#DC2626', label: 'Planifiés >5J ouvrés' },
        ].map((l, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 10, height: 10, background: l.color, borderRadius: 2 }} />
            <span style={{ color: 'var(--text3)' }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Carte indicateur ──────────────────────────────────────────────────────────
function IndicatorCard({ id, label, value, month, trend, objective }) {
  const ok   = value != null && value >= objective;
  const color = value == null ? 'var(--text3)' : ok ? '#059669' : '#DC2626';

  return (
    <div style={{
      background: ok ? '#F0FDF4' : value == null ? 'var(--bg2)' : '#FFF5F5',
      border: `2px solid ${ok ? '#86EFAC' : value == null ? 'var(--border)' : '#FECACA'}`,
      borderRadius: 14, padding: '18px 20px',
      display: 'flex', flexDirection: 'column', gap: 6, minWidth: 180,
    }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1 }}>{id}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', lineHeight: 1.3 }}>{label}</div>
      <div style={{ fontSize: 36, fontWeight: 900, color, lineHeight: 1 }}>
        {value != null ? `${value}%` : '—'}
      </div>
      {month && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{month}</div>}
      {trend != null && (
        <div style={{ fontSize: 12, fontWeight: 700, color: trend >= 0 ? '#059669' : '#DC2626' }}>
          {trend >= 0 ? '↑' : '↓'} {Math.abs(trend)}% vs mois précédent
        </div>
      )}
      <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
        Objectif : ≥{objective}%
      </div>
    </div>
  );
}

// ── Sélecteur de mois (multi-sélection par range) ────────────────────────────
function MonthRangeSelector({ available, selected, onChange }) {
  if (!available?.length) return null;

  const sorted = [...available].sort(); // croissant
  const first = sorted[0];
  const last  = sorted[sorted.length - 1];

  // Générer tous les mois dans la plage disponible
  const allMonths = [];
  let cur = new Date(first + '-01');
  const end = new Date(last + '-01');
  while (cur <= end) {
    const key = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,'0')}`;
    if (available.includes(key)) allMonths.push(key);
    cur.setMonth(cur.getMonth() + 1);
  }

  const toggle = (m) => {
    if (selected.includes(m)) onChange(selected.filter(x => x !== m));
    else onChange([...selected, m].sort());
  };

  const selectLast = (n) => {
    const recent = sorted.slice(-n);
    onChange(recent);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8, flexWrap: 'wrap' }}>
        {[3, 6, 12].map(n => (
          <button key={n} onClick={() => selectLast(n)} style={{
            padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border2)',
            background: 'var(--bg3)', color: 'var(--text3)',
          }}>
            {n} derniers mois
          </button>
        ))}
        <button onClick={() => onChange(sorted)} style={{
          padding: '4px 10px', borderRadius: 16, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border2)',
          background: 'var(--bg3)', color: 'var(--text3)',
        }}>
          Tout
        </button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {allMonths.map(m => (
          <button key={m} onClick={() => toggle(m)} style={{
            padding: '5px 12px', borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
            background: selected.includes(m) ? '#185FA5' : 'var(--bg3)',
            color: selected.includes(m) ? '#fff' : 'var(--text3)',
            transition: 'all .15s',
          }}>
            {monthLabel(m)}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Tableau de détail (drill-down par clic) ───────────────────────────────────
const DD_COLS = {
  i001: [
    { key: 'ref_commande',   label: 'Réf commande' },
    { key: 'ref_work_order', label: 'Work Order' },
    { key: 'client',         label: 'Client' },
    { key: 'code_postal',    label: 'Code postal' },
    { key: 'technicien',     label: 'Technicien' },
    { key: 'date_entree',    label: 'Date entrée AP' },
    { key: 'date_sortie',    label: 'Date planif.' },
    { key: 'stage_sortie',   label: 'Étape' },
    { key: 'delai',          label: 'Délai' },
  ],
  i002: [
    { key: 'ref_commande',     label: 'Réf commande' },
    { key: 'ref_work_order',   label: 'Work Order' },
    { key: 'client',           label: 'Client' },
    { key: 'code_postal',      label: 'Code postal' },
    { key: 'technicien',       label: 'Technicien' },
    { key: 'date_planifiee',   label: 'Date entrée AP' },
    { key: 'date_intervention',label: 'Date inter.' },
    { key: 'delai',            label: 'Délai' },
  ],
  i003: [
    { key: 'ref_commande',     label: 'Réf commande' },
    { key: 'ref_work_order',   label: 'Work Order' },
    { key: 'client',           label: 'Client' },
    { key: 'code_postal',      label: 'Code postal' },
    { key: 'technicien',       label: 'Technicien' },
    { key: 'date_planifiee',   label: 'Date planif.' },
    { key: 'date_erdv',        label: 'Date E-RDV' },
    { key: 'date_intervention',label: 'Date inter.' },
  ],
  i004: [
    { key: 'ref_commande',     label: 'Réf commande' },
    { key: 'ref_work_order',   label: 'Work Order' },
    { key: 'client',           label: 'Client' },
    { key: 'code_postal',      label: 'Code postal' },
    { key: 'technicien',       label: 'Technicien' },
    { key: 'date',             label: 'Date' },
    { key: 'date_intervention',label: 'Date inter.' },
  ],
  i005: [
    { key: 'ref_commande',     label: 'Réf commande' },
    { key: 'ref_work_order',   label: 'Work Order' },
    { key: 'client',           label: 'Client' },
    { key: 'code_postal',      label: 'Code postal' },
    { key: 'technicien',       label: 'Technicien' },
    { key: 'date_intervention',label: 'Date inter.' },
    { key: 'date_cri',         label: 'Date CRI' },
    { key: 'delai',            label: 'Délai émission' },
  ],
  i006: [
    { key: 'ref_commande',     label: 'Réf commande' },
    { key: 'ref_work_order',   label: 'Work Order' },
    { key: 'client',           label: 'Client' },
    { key: 'code_postal',      label: 'Code postal' },
    { key: 'technicien',       label: 'Technicien' },
    { key: 'date_intervention',label: 'Date inter.' },
    { key: 'date_soumission',  label: 'Date soumission' },
    { key: 'delai',            label: 'Délai' },
    { key: 'type_doc',         label: 'Type doc.' },
  ],
  i007: [
    { key: 'ref_commande',     label: 'Réf commande' },
    { key: 'ref_work_order',   label: 'Work Order' },
    { key: 'client',           label: 'Client' },
    { key: 'code_postal',      label: 'Code postal' },
    { key: 'technicien',       label: 'Technicien' },
    { key: 'date',             label: 'Date soumission' },
    { key: 'resultat',         label: 'Résultat' },
  ],
};

function DrilldownTable({ kpi, month, bucket, label, onClose }) {
  const [items,   setItems]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get('/admin/kpi/detail', { params: { kpi, month, bucket } })
      .then(r  => setItems(r.data.items))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [kpi, month, bucket]);

  const cols = DD_COLS[kpi] || [];
  const MOIS_LONG2 = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
  const mLabel = month ? (() => { const [y,m] = month.split('-'); return `${MOIS_LONG2[parseInt(m,10)-1]} ${y}`; })() : '';

  return (
    <div style={{ marginTop: 16, border: '2px solid #3B82F6', borderRadius: 12, overflow: 'hidden', background: 'var(--bg)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', background: '#EFF6FF', borderBottom: '1px solid #BFDBFE' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1D4ED8' }}>
          {label} — {mLabel}
          {items && !loading && (
            <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 600, color: '#6B7280' }}>
              ({items.length} dossier{items.length !== 1 ? 's' : ''})
            </span>
          )}
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#6B7280', padding: '0 4px', lineHeight: 1 }}>✕</button>
      </div>
      {loading ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)' }}>Chargement…</div>
      ) : !items?.length ? (
        <div style={{ padding: 20, textAlign: 'center', color: 'var(--text3)' }}>Aucun dossier trouvé</div>
      ) : (
        <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--bg3)', position: 'sticky', top: 0, zIndex: 1 }}>
                {cols.map(c => (
                  <th key={c.key} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .4, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                    {c.label}
                  </th>
                ))}
                <th style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }} />
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? 'var(--bg2)' : 'transparent', borderBottom: '1px solid var(--border)' }}>
                  {cols.map(c => (
                    <td key={c.key} style={{ padding: '7px 12px', color: 'var(--text)', whiteSpace: 'nowrap' }}>
                      {item[c.key] != null && item[c.key] !== '' ? String(item[c.key]) : <span style={{ color: 'var(--text3)' }}>—</span>}
                    </td>
                  ))}
                  <td style={{ padding: '4px 12px', whiteSpace: 'nowrap' }}>
                    {item.zoho_id ? (
                      <a
                        href={`https://crmplus.zoho.eu/valeatelecom/index.do/cxapp/crm/org20082707825/tab/Potentials/${item.zoho_id}`}
                        target="_blank" rel="noreferrer"
                        style={{ display: 'inline-block', padding: '3px 8px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.35)', borderRadius: 6, color: '#2563EB', fontSize: 11, fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}
                      >↗ Zoho</a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Helper : toggle drilldown (même cellule = ferme)
function toggleDD(prev, next) {
  if (prev?.month === next.month && prev?.bucket === next.bucket) return null;
  return next;
}

// Style pour cellule cliquable dans les tableaux
const clickableCell = (active) => ({
  cursor: 'pointer',
  padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 700,
  outline: active ? '2px solid #2563EB' : 'none',
  outlineOffset: 1,
  transition: 'filter .15s',
});

// ── I001 Panel ────────────────────────────────────────────────────────────────
function I001Panel() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [available, setAvailable] = useState([]);
  // Le chargement initial fixe la selection par defaut ; sans ce drapeau, la
  // closure figee du useCallback la reappliquait a chaque changement de mois.
  const initFait = useRef(false);
  const [selMonths, setSelMonths] = useState([]);
  const [drilldown, setDrilldown] = useState(null);

  const load = useCallback(async (months) => {
    setLoading(true);
    try {
      const params = months?.length ? { months: months.join(',') } : {};
      const res = await api.get('/admin/kpi/i001', { params });
      setData(res.data);
      if (res.data.availableMonths?.length && !initFait.current) {
        initFait.current = true;
        setAvailable(res.data.availableMonths.slice().sort());
        // Sélection par défaut : 6 derniers mois
        const last6 = res.data.availableMonths.slice().sort().slice(-6);
        setSelMonths(last6);
        // Recharger avec la sélection
        const r2 = await api.get('/admin/kpi/i001', { params: { months: last6.join(',') } });
        setData(r2.data);
      }
    } catch (e) { setData(null); signalerErreur(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load([]); }, []); // eslint-disable-line

  const handleMonthChange = (months) => {
    setSelMonths(months);
    load(months);
  };

  const series = data?.series || [];

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
        {/* Carte indicateur */}
        <IndicatorCard
          id="I001"
          label="Délai de Prise de RDV"
          value={data?.indicator}
          month={data?.indicator_month}
          trend={data?.trend}
          objective={90}
        />
        {/* Description */}
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: 'var(--text)' }}>I001 — Délai de Prise de RDV</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 10 }}>
            Mesure le délai en jours ouvrés entre la réception d'une commande (statut "A planifier") et sa planification (passage à "Planifiée").<br />
            <strong>Hors SAV.</strong> Objectif : ≥ 90% des interventions planifiées en 2 jours ouvrés ou moins. Le délai se compte en journées : reçue lundi, une commande planifiée jusqu'au mercredi inclus respecte le critère.
          </div>
          {/* Totaux sur la période sélectionnée */}
          {series.length > 0 && (() => {
            const totals = series.reduce((acc, s) => ({
              total: acc.total + s.total,
              lt2:   acc.lt2   + s.lt2,
              gte2:  acc.gte2  + s.gte2,
              gt5:   acc.gt5   + s.gt5,
            }), { total: 0, lt2: 0, gte2: 0, gt5: 0 });
            const taux = totals.total > 0 ? Math.round((totals.lt2 / totals.total) * 1000) / 10 : 0;
            return (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[
                  { label: 'Total planifiés', val: totals.total, color: 'var(--text)' },
                  { label: '≤2J ouvrés', val: totals.lt2, color: '#2563EB' },
                  { label: '>2 à 5J ouvrés', val: totals.gte2, color: '#F59E0B' },
                  { label: '>5J ouvrés', val: totals.gt5, color: '#DC2626' },
                  { label: 'Taux <2J', val: `${taux}%`, color: taux >= 90 ? '#059669' : '#DC2626' },
                ].map((s, i) => (
                  <div key={i} style={{ background: 'var(--bg2)', borderRadius: 8, padding: '6px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: s.color }}>{s.val}</div>
                    <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Sélecteur de mois */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>
          Sélection des mois affichés
        </div>
        <MonthRangeSelector available={available} selected={selMonths} onChange={handleMonthChange} />
      </div>

      {/* Graphique */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 30, color: 'var(--text3)' }}>Chargement…</div>
      ) : (
        <BarChart series={series} objective={90} />
      )}

      {/* Tableau détaillé */}
      {!loading && series.length > 0 && (
        <div style={{ marginTop: 20, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '120px 80px 100px 100px 100px 110px',
            padding: '8px 14px', background: 'var(--bg3)',
            fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .5,
            borderBottom: '1px solid var(--border)',
          }}>
            <div>Mois</div>
            <div style={{ textAlign: 'center' }}>Total</div>
            <div style={{ textAlign: 'center' }}>{'<'}2J ouvrés</div>
            <div style={{ textAlign: 'center' }}>2-5J ouvrés</div>
            <div style={{ textAlign: 'center' }}>{'>'}5J ouvrés</div>
            <div style={{ textAlign: 'center' }}>Taux {'<'}2J</div>
          </div>
          {series.map((s, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '120px 80px 100px 100px 100px 110px',
              padding: '10px 14px', alignItems: 'center',
              background: i % 2 === 0 ? 'var(--bg2)' : 'transparent',
              borderBottom: i < series.length - 1 ? '1px solid var(--border)' : 'none',
              fontSize: 13,
            }}>
              <div style={{ fontWeight: 600 }}>{s.label}</div>
              <div style={{ textAlign: 'center', fontWeight: 700 }}>{s.total}</div>
              <div style={{ textAlign: 'center' }}>
                <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'lt2', label: '≤2J ouvrés' }))}
                  style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='lt2'), background: '#DBEAFE', color: '#1D4ED8' }}>{s.lt2}</span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'gte2', label: '>2 à 5J ouvrés' }))}
                  style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='gte2'), background: '#FEF3C7', color: '#92400E' }}>{s.gte2}</span>
              </div>
              <div style={{ textAlign: 'center' }}>
                {s.gt5 > 0
                  ? <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'gt5', label: '>5J ouvrés' }))}
                      style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='gt5'), background: '#FEE2E2', color: '#991B1B' }}>{s.gt5}</span>
                  : <span style={{ color: 'var(--text3)' }}>0</span>}
              </div>
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontWeight: 800, fontSize: 14, color: s.taux_lt2 >= 90 ? '#059669' : '#DC2626' }}>{s.taux_lt2}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {drilldown && <DrilldownTable kpi="i001" month={drilldown.month} bucket={drilldown.bucket} label={drilldown.label} onClose={() => setDrilldown(null)} />}
    </div>
  );
}

// ── I002 Panel ────────────────────────────────────────────────────────────────
function I002Panel() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [available, setAvailable] = useState([]);
  // Le chargement initial fixe la selection par defaut ; sans ce drapeau, la
  // closure figee du useCallback la reappliquait a chaque changement de mois.
  const initFait = useRef(false);
  const [selMonths, setSelMonths] = useState([]);
  const [drilldown, setDrilldown] = useState(null);

  const load = useCallback(async (months) => {
    setLoading(true);
    try {
      const params = months?.length ? { months: months.join(',') } : {};
      const res = await api.get('/admin/kpi/i002', { params });
      setData(res.data);
      if (res.data.availableMonths?.length && !initFait.current) {
        initFait.current = true;
        const sorted = res.data.availableMonths.slice().sort();
        setAvailable(sorted);
        const last6 = sorted.slice(-6);
        setSelMonths(last6);
        const r2 = await api.get('/admin/kpi/i002', { params: { months: last6.join(',') } });
        setData(r2.data);
      }
    } catch (e) { setData(null); signalerErreur(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load([]); }, []); // eslint-disable-line

  const handleMonthChange = (months) => { setSelMonths(months); load(months); };
  const series = data?.series || [];
  const objective = 80;

  // Totaux période
  const totals = series.reduce((acc, s) => ({
    total: acc.total + s.total,
    j1j15: acc.j1j15 + s.j1j15,
    gt15:  acc.gt15  + s.gt15,
  }), { total: 0, j1j15: 0, gt15: 0 });
  const tauxGlobal = totals.total > 0 ? Math.round((totals.j1j15 / totals.total) * 1000) / 10 : 0;
  const maxTotal = Math.max(...series.map(s => s.total), 1);

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
        <IndicatorCard
          id="I002"
          label="Délai d'intervention"
          value={data?.indicator}
          month={data?.indicator_month}
          trend={data?.trend}
          objective={objective}
        />
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: 'var(--text)' }}>I002 — Délai d'intervention</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, marginBottom: 10 }}>
            Délai entre la date de planification du RDV (Planifiée) et la date d'intervention.<br />
            <strong>Production uniquement, hors SAV, hors E-RDV.</strong> Objectif : ≥ {objective}% des interventions dans les J+15. Délai compté en <strong>jours ouvrés</strong>, week-ends et jours fériés exclus.
          </div>
          {series.length > 0 && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {[
                { label: 'Total', val: totals.total, color: 'var(--text)' },
                { label: 'J+1 à J+15', val: totals.j1j15, color: '#2563EB' },
                { label: 'Au-delà J+15', val: totals.gt15, color: '#DC2626' },
                { label: 'Taux ≤J+15', val: `${tauxGlobal}%`, color: tauxGlobal >= objective ? '#059669' : '#DC2626' },
              ].map((s, i) => (
                <div key={i} style={{ background: 'var(--bg2)', borderRadius: 8, padding: '6px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: s.color }}>{s.val}</div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 600 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sélecteur de mois */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>
          Sélection des mois affichés
        </div>
        <MonthRangeSelector available={available} selected={selMonths} onChange={handleMonthChange} />
      </div>

      {/* Graphique */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 30, color: 'var(--text3)' }}>Chargement…</div>
      ) : !series.length ? (
        <div style={{ color: 'var(--text3)', fontSize: 13, padding: 20 }}>Aucune donnée</div>
      ) : (
        <div style={{ position: 'relative' }}>
          {/* Ligne objectif */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 28, pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: `${100 - objective}%`, left: 0, right: 0, borderTop: '2px dashed #DC2626', zIndex: 2 }}>
              <span style={{ position: 'absolute', right: 0, top: -16, fontSize: 10, color: '#DC2626', fontWeight: 700, background: 'var(--bg)', padding: '1px 4px' }}>
                Objectif {objective}%
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 200, paddingBottom: 28, position: 'relative' }}>
            {series.map((s, i) => {
              const h = (n) => Math.max((n / maxTotal) * 160, n > 0 ? 3 : 0);
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: s.taux_j15 >= objective ? '#059669' : '#DC2626', marginBottom: 3 }}>
                    {s.taux_j15}%
                  </div>
                  <div style={{ width: '100%', display: 'flex', flexDirection: 'column-reverse', gap: 1 }}>
                    <div style={{ width: '100%', height: h(s.j1j15), background: '#2563EB', borderRadius: '2px 2px 0 0', transition: 'height .4s' }} title={`J+1→J+15: ${s.j1j15}`} />
                    <div style={{ width: '100%', height: h(s.gt15), background: '#DC2626', transition: 'height .4s' }} title={`>J+15: ${s.gt15}`} />
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{s.total}</div>
                  <div style={{ position: 'absolute', bottom: 0, fontSize: 10, color: 'var(--text3)', fontWeight: 600, left: `${(i / series.length) * 100}%`, width: `${100 / series.length}%`, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {s.label}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11 }}>
            {[{ color: '#2563EB', label: 'J+1 à J+15' }, { color: '#DC2626', label: 'Au-delà de J+15' }].map((l, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 10, height: 10, background: l.color, borderRadius: 2 }} />
                <span style={{ color: 'var(--text3)' }}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tableau détaillé */}
      {!loading && series.length > 0 && (
        <div style={{ marginTop: 20, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '130px 80px 120px 120px 110px',
            padding: '8px 14px', background: 'var(--bg3)',
            fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .5,
            borderBottom: '1px solid var(--border)',
          }}>
            <div>Mois</div>
            <div style={{ textAlign: 'center' }}>Total</div>
            <div style={{ textAlign: 'center' }}>J+1 à J+15</div>
            <div style={{ textAlign: 'center' }}>Au-delà J+15</div>
            <div style={{ textAlign: 'center' }}>Taux ≤J+15</div>
          </div>
          {series.map((s, i) => (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '130px 80px 120px 120px 110px',
              padding: '10px 14px', alignItems: 'center',
              background: i % 2 === 0 ? 'var(--bg2)' : 'transparent',
              borderBottom: i < series.length - 1 ? '1px solid var(--border)' : 'none',
              fontSize: 13,
            }}>
              <div style={{ fontWeight: 600 }}>{s.label}</div>
              <div style={{ textAlign: 'center', fontWeight: 700 }}>{s.total}</div>
              <div style={{ textAlign: 'center' }}>
                <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'j1j15', label: 'J+1 à J+15' }))}
                  style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='j1j15'), background: '#DBEAFE', color: '#1D4ED8' }}>{s.j1j15}</span>
              </div>
              <div style={{ textAlign: 'center' }}>
                {s.gt15 > 0
                  ? <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'gt15', label: 'Au-delà J+15' }))}
                      style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='gt15'), background: '#FEE2E2', color: '#991B1B' }}>{s.gt15}</span>
                  : <span style={{ color: 'var(--text3)' }}>0</span>}
              </div>
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontWeight: 800, fontSize: 14, color: s.taux_j15 >= objective ? '#059669' : '#DC2626' }}>{s.taux_j15}%</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {drilldown && <DrilldownTable kpi="i002" month={drilldown.month} bucket={drilldown.bucket} label={drilldown.label} onClose={() => setDrilldown(null)} />}
    </div>
  );
}

// ── I003 Chart — fidèle au template Covage ───────────────────────────────────
// Barres grises (total inter) + barres bleues (CMD E-RDV) + ligne verte (taux) + ligne bleue basse (non respectés)
function I003Chart({ series, objective }) {
  if (!series?.length) return <div style={{ color: 'var(--text3)', fontSize: 13, padding: 20 }}>Aucune donnée</div>;

  const maxBar  = Math.max(...series.map(s => s.total_inter), 1);
  const CHART_H = 220; // hauteur zone barres (px)
  const BAR_H   = 170; // hauteur max barre

  // Points de la ligne verte (taux, axe droit 80-110%)
  const TAUX_MIN = 80, TAUX_MAX = 110;
  const tauxToY = (t) => {
    if (t == null) return null;
    const pct = (t - TAUX_MIN) / (TAUX_MAX - TAUX_MIN); // 0=bas, 1=haut
    return CHART_H - 30 - pct * BAR_H; // inversé : haut = Y petit
  };

  const n = series.length;
  const W = 100 / n; // % de largeur par mois

  return (
    <div>
      {/* Zone graphique SVG */}
      <div style={{ position: 'relative', height: CHART_H + 24 }}>
        {/* Axe droit — taux labels */}
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 24, width: 52, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          {[110, 100, 90, 80].map(v => (
            <div key={v} style={{ fontSize: 9, color: 'var(--text3)', lineHeight: 1 }}>{v}%</div>
          ))}
        </div>

        {/* Zone barres */}
        <div style={{ position: 'absolute', left: 0, right: 56, top: 0, bottom: 24, background: 'rgba(255,240,180,0.15)', borderRadius: 8 }}>
          {/* Grille horizontale */}
          {[0, 25, 50, 75, 100].map(p => (
            <div key={p} style={{ position: 'absolute', top: `${100 - p}%`, left: 0, right: 0, borderTop: '1px solid var(--border)', opacity: .4 }} />
          ))}

          {/* Barres + labels par mois */}
          <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%', gap: 6, padding: '0 4px' }}>
            {series.map((s, i) => {
              const hTotal = (s.total_inter / maxBar) * BAR_H;
              const hErdv  = (s.total_erdv  / maxBar) * BAR_H;
              const taux   = s.taux_respect;
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', height: '100%' }}>
                  {/* Nb total inter label */}
                  <div style={{ position: 'absolute', bottom: hTotal + 2, fontSize: 10, fontWeight: 700, color: '#555', whiteSpace: 'nowrap' }}>
                    {s.total_inter > 0 ? s.total_inter : ''}
                  </div>
                  {/* Barres côte à côte */}
                  <div style={{ position: 'absolute', bottom: 0, width: '100%', display: 'flex', gap: 2, alignItems: 'flex-end', justifyContent: 'center' }}>
                    {/* Barre grise — total inter */}
                    <div style={{ width: '44%', height: hTotal, background: '#9CA3AF', borderRadius: '3px 3px 0 0', transition: 'height .4s' }} title={`Total inter: ${s.total_inter}`} />
                    {/* Barre bleue — CMD E-RDV */}
                    <div style={{ width: '44%', height: hErdv, background: '#38BDF8', borderRadius: '3px 3px 0 0', transition: 'height .4s' }} title={`CMD E-RDV: ${s.total_erdv}`} />
                  </div>
                  {/* Nb E-RDV label */}
                  {s.total_erdv > 0 && (
                    <div style={{ position: 'absolute', bottom: hErdv + 2, right: '6%', fontSize: 10, fontWeight: 700, color: '#0284C7', whiteSpace: 'nowrap' }}>
                      {s.total_erdv}
                    </div>
                  )}
                  {/* Non respectés (en rouge, tout en bas) */}
                  {s.non_respecte > 0 && (
                    <div style={{ position: 'absolute', bottom: 0, fontSize: 9, fontWeight: 700, color: '#DC2626', whiteSpace: 'nowrap', zIndex: 3 }}>
                      -{s.non_respecte}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Ligne verte — taux (SVG overlay) */}
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
            {series.map((s, i) => {
              if (s.taux_respect == null) return null;
              const x    = ((i + 0.5) / n) * 100;
              const y    = (tauxToY(s.taux_respect) / (CHART_H - 24)) * 100;
              const col  = s.taux_respect >= objective ? '#059669' : '#DC2626';
              const lbl  = (v, c) => <text key={`t${i}`} x={`${v.x}%`} y={`${v.y}%`} dy="-10" textAnchor="middle" fontSize="10" fontWeight="800" fill={c} stroke="white" strokeWidth="3" paintOrder="stroke">{v.taux}%</text>;
              const nextS = series[i + 1];
              if (!nextS || nextS.taux_respect == null) return (
                <g key={i}>
                  <circle cx={`${x}%`} cy={`${y}%`} r={4} fill="#16A34A" />
                  {lbl({ x, y, taux: s.taux_respect }, col)}
                </g>
              );
              const nx   = ((i + 1.5) / n) * 100;
              const ny   = (tauxToY(nextS.taux_respect) / (CHART_H - 24)) * 100;
              const colN = nextS.taux_respect >= objective ? '#059669' : '#DC2626';
              return (
                <g key={i}>
                  <line x1={`${x}%`} y1={`${y}%`} x2={`${nx}%`} y2={`${ny}%`} stroke="#16A34A" strokeWidth={2.5} />
                  <circle cx={`${x}%`} cy={`${y}%`} r={4} fill="#16A34A" />
                  {lbl({ x, y, taux: s.taux_respect }, col)}
                  {i === series.length - 2 && <circle cx={`${nx}%`} cy={`${ny}%`} r={4} fill="#16A34A" />}
                  {i === series.length - 2 && lbl({ x: nx, y: ny, taux: nextS.taux_respect }, colN)}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Labels mois en bas */}
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 56, display: 'flex' }}>
          {series.map((s, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: 'var(--text3)', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {s.label}
            </div>
          ))}
        </div>
      </div>

      {/* Légende */}
      <div style={{ display: 'flex', gap: 18, marginTop: 10, fontSize: 11, flexWrap: 'wrap' }}>
        {[
          { color: '#9CA3AF', label: 'Nb inter mensuel', type: 'bar' },
          { color: '#38BDF8', label: 'Nb CMD avec E-RDV', type: 'bar' },
          { color: '#DC2626', label: 'Nb E-RDV non respectés', type: 'text' },
          { color: '#16A34A', label: 'Taux respect E-RDV', type: 'line' },
        ].map((l, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {l.type === 'bar'  && <div style={{ width: 10, height: 10, background: l.color, borderRadius: 2 }} />}
            {l.type === 'line' && <div style={{ width: 16, height: 3, background: l.color, borderRadius: 2 }} />}
            {l.type === 'text' && <div style={{ fontSize: 10, fontWeight: 800, color: l.color }}>-N</div>}
            <span style={{ color: 'var(--text3)' }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── I003 Panel ────────────────────────────────────────────────────────────────
function I003Panel() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [available, setAvailable] = useState([]);
  // Le chargement initial fixe la selection par defaut ; sans ce drapeau, la
  // closure figee du useCallback la reappliquait a chaque changement de mois.
  const initFait = useRef(false);
  const [selMonths, setSelMonths] = useState([]);
  const [drilldown, setDrilldown] = useState(null);

  const load = useCallback(async (months) => {
    setLoading(true);
    try {
      const params = months?.length ? { months: months.join(',') } : {};
      const res = await api.get('/admin/kpi/i003', { params });
      setData(res.data);
      if (res.data.availableMonths?.length && !initFait.current) {
        initFait.current = true;
        const sorted = res.data.availableMonths.slice().sort();
        setAvailable(sorted);
        const last6 = sorted.slice(-6);
        setSelMonths(last6);
        const r2 = await api.get('/admin/kpi/i003', { params: { months: last6.join(',') } });
        setData(r2.data);
      }
    } catch (e) { setData(null); signalerErreur(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load([]); }, []); // eslint-disable-line

  const handleMonthChange = (months) => { setSelMonths(months); load(months); };
  const series    = data?.series || [];
  const objective = 95;

  const totals = series.reduce((acc, s) => ({
    total_inter:   acc.total_inter   + s.total_inter,
    total_erdv:    acc.total_erdv    + s.total_erdv,
    respecte:      acc.respecte      + s.respecte,
    non_respecte:  acc.non_respecte  + s.non_respecte,
  }), { total_inter: 0, total_erdv: 0, respecte: 0, non_respecte: 0 });
  const tauxGlobal = totals.total_erdv > 0 ? Math.round((totals.respecte / totals.total_erdv) * 1000) / 10 : null;

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* Graphique principal */}
      <div className="card" style={{ flex: 1, minWidth: 500 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: 'var(--text)' }}>
          Indicateur I003 / Respect des E-RDV
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14 }}>Nb interventions/mois</div>

        {/* Sélecteur de mois */}
        <div style={{ marginBottom: 14 }}>
          <MonthRangeSelector available={available} selected={selMonths} onChange={handleMonthChange} />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text3)' }}>Chargement…</div>
        ) : !series.length ? (
          <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: 24, textAlign: 'center' }}>
            <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>Aucune donnée E-RDV</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.6 }}>
              Le champ <code>date_erdv</code> est enregistré à partir de maintenant.<br />
              Les prochains imports avec E-RDV apparaîtront ici.
            </div>
          </div>
        ) : (
          <I003Chart series={series} objective={objective} />
        )}

        {/* Tableau détaillé */}
        {!loading && series.length > 0 && (
          <div style={{ marginTop: 20, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '110px 90px 100px 110px 120px 110px',
              padding: '8px 12px', background: 'var(--bg3)',
              fontSize: 9, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .4,
              borderBottom: '1px solid var(--border)',
            }}>
              <div>Mois</div>
              <div style={{ textAlign: 'center' }}>Total inter</div>
              <div style={{ textAlign: 'center' }}>CMD E-RDV</div>
              <div style={{ textAlign: 'center' }}>Respectés</div>
              <div style={{ textAlign: 'center' }}>Non respectés</div>
              <div style={{ textAlign: 'center' }}>Taux respect</div>
            </div>
            {series.map((s, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '110px 90px 100px 110px 120px 110px',
                padding: '9px 12px', alignItems: 'center',
                background: i % 2 === 0 ? 'var(--bg2)' : 'transparent',
                borderBottom: i < series.length - 1 ? '1px solid var(--border)' : 'none',
                fontSize: 13,
              }}>
                <div style={{ fontWeight: 600 }}>{s.label}</div>
                <div style={{ textAlign: 'center' }}>
                  <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'total', label: 'Toutes inter.' }))}
                    style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='total'), background: 'var(--bg3)', color: '#6B7280' }}>{s.total_inter}</span>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <span style={{ background: '#E0F2FE', color: '#0369A1', padding: '2px 7px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>{s.total_erdv}</span>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'respecte', label: 'E-RDV respectés' }))}
                    style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='respecte'), background: '#D1FAE5', color: '#065F46' }}>{s.respecte}</span>
                </div>
                <div style={{ textAlign: 'center' }}>
                  {s.non_respecte > 0
                    ? <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'non_respecte', label: 'E-RDV non respectés' }))}
                        style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='non_respecte'), background: '#FEE2E2', color: '#991B1B' }}>{s.non_respecte}</span>
                    : <span style={{ color: 'var(--text3)' }}>0</span>}
                </div>
                <div style={{ textAlign: 'center' }}>
                  {s.taux_respect != null
                    ? <span style={{ fontWeight: 800, fontSize: 14, color: s.taux_respect >= objective ? '#059669' : '#DC2626' }}>{s.taux_respect}%</span>
                    : <span style={{ color: 'var(--text3)' }}>—</span>}
                </div>
              </div>
            ))}
          </div>
        )}
        {drilldown && <DrilldownTable kpi="i003" month={drilldown.month} bucket={drilldown.bucket} label={drilldown.label} onClose={() => setDrilldown(null)} />}
      </div>

      {/* Panel droit — indicateur + tendance */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 200 }}>
        <div className="card" style={{ textAlign: 'center', padding: '24px 28px' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Indicateur I003</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>Taux de respect de l'indicateur :</div>
          <div style={{ fontSize: 42, fontWeight: 900, color: data?.indicator >= objective ? '#059669' : data?.indicator != null ? '#DC2626' : 'var(--text3)', lineHeight: 1 }}>
            {data?.indicator != null ? `${data.indicator}%` : '—'}
          </div>
          {data?.indicator_month && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>{data.indicator_month}</div>}
        </div>

        <div className="card" style={{ textAlign: 'center', padding: '20px 28px' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Tendance de l'indicateur I003</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>Tendance sur M-1 :</div>
          <div style={{ fontSize: 36, fontWeight: 900, color: data?.trend >= 0 ? '#059669' : '#DC2626', lineHeight: 1 }}>
            {data?.trend != null ? `${data.trend >= 0 ? '+' : ''}${data.trend}%` : '—'}
          </div>
        </div>

        {/* Totaux période */}
        {totals.total_erdv > 0 && (
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Période sélectionnée</div>
            {[
              { label: 'Total inter', val: totals.total_inter, color: '#6B7280' },
              { label: 'CMD E-RDV', val: totals.total_erdv, color: '#0284C7' },
              { label: 'E-RDV respectés', val: totals.respecte, color: '#059669' },
              { label: 'Non respectés', val: totals.non_respecte, color: '#DC2626' },
              { label: 'Taux global', val: tauxGlobal != null ? `${tauxGlobal}%` : '—', color: tauxGlobal >= objective ? '#059669' : '#DC2626' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: i < 4 ? '1px solid var(--border)' : 'none' }}>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>{s.label}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: s.color }}>{s.val}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── I004 Panel ────────────────────────────────────────────────────────────────
function I004Panel() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [available, setAvailable] = useState([]);
  // Le chargement initial fixe la selection par defaut ; sans ce drapeau, la
  // closure figee du useCallback la reappliquait a chaque changement de mois.
  const initFait = useRef(false);
  const [selMonths, setSelMonths] = useState([]);
  const [drilldown, setDrilldown] = useState(null);

  const load = useCallback(async (months) => {
    setLoading(true);
    try {
      const params = months?.length ? { months: months.join(',') } : {};
      const res = await api.get('/admin/kpi/i004', { params });
      setData(res.data);
      if (res.data.availableMonths?.length && !initFait.current) {
        initFait.current = true;
        const sorted = res.data.availableMonths.slice().sort();
        setAvailable(sorted);
        const last6 = sorted.slice(-6);
        setSelMonths(last6);
        const r2 = await api.get('/admin/kpi/i004', { params: { months: last6.join(',') } });
        setData(r2.data);
      }
    } catch (e) { setData(null); signalerErreur(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load([]); }, []); // eslint-disable-line

  const handleMonthChange = (months) => { setSelMonths(months); load(months); };
  const series    = data?.series || [];
  const objective = 95; // taux respect ≥ 95% = taux RVNH ≤ 5%

  const totals = series.reduce((acc, s) => ({
    planifiee: acc.planifiee + s.planifiee,
    rvnh:      acc.rvnh     + s.rvnh,
  }), { planifiee: 0, rvnh: 0 });
  const tauxRvnhGlobal    = totals.planifiee > 0 ? Math.round((totals.rvnh / totals.planifiee) * 10000) / 100 : 0;
  const tauxRespectGlobal = Math.round((100 - tauxRvnhGlobal) * 100) / 100;

  const maxBar = Math.max(...series.map(s => s.planifiee), 1);
  const CHART_H = 220;
  const BAR_H   = 170;

  // Ligne orange : taux_rvnh, axe droit 0-50%
  const TAUX_MIN = 0, TAUX_MAX = 50;
  const tauxToY = (t) => {
    const pct = Math.min(t, TAUX_MAX) / TAUX_MAX; // 0=bas, 1=haut
    return CHART_H - 30 - pct * BAR_H;
  };

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* Graphique principal */}
      <div className="card" style={{ flex: 1, minWidth: 500 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: 'var(--text)' }}>
          Indicateur I004 / Respect des RDV = Nombre de « RVNH »
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14 }}>Nb interventions/mois</div>

        <div style={{ marginBottom: 14 }}>
          <MonthRangeSelector available={available} selected={selMonths} onChange={handleMonthChange} />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text3)' }}>Chargement…</div>
        ) : !series.length ? (
          <div style={{ color: 'var(--text3)', fontSize: 13, padding: 20 }}>Aucune donnée</div>
        ) : (
          <div>
            <div style={{ position: 'relative', height: CHART_H + 24 }}>
              {/* Axe droit — taux RVNH labels */}
              <div style={{ position: 'absolute', right: 0, top: 0, bottom: 24, width: 52, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                {[50, 40, 30, 20, 10, 0].map(v => (
                  <div key={v} style={{ fontSize: 9, color: 'var(--text3)', lineHeight: 1 }}>{v}%</div>
                ))}
              </div>

              {/* Zone barres */}
              <div style={{ position: 'absolute', left: 0, right: 56, top: 0, bottom: 24, background: 'rgba(180,120,220,0.07)', borderRadius: 8 }}>
                {/* Grille */}
                {[0, 25, 50, 75, 100].map(p => (
                  <div key={p} style={{ position: 'absolute', top: `${100 - p}%`, left: 0, right: 0, borderTop: '1px solid var(--border)', opacity: .4 }} />
                ))}

                <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%', gap: 6, padding: '0 4px' }}>
                  {series.map((s, i) => {
                    const hBlue = (s.planifiee / maxBar) * BAR_H;
                    const hRed  = Math.max((s.rvnh / maxBar) * BAR_H, s.rvnh > 0 ? 6 : 0);
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', height: '100%' }}>
                        {/* Nb inter label */}
                        <div style={{ position: 'absolute', bottom: hBlue + 3, fontSize: 10, fontWeight: 700, color: '#1D4ED8', whiteSpace: 'nowrap' }}>
                          {s.planifiee}
                        </div>
                        {/* Barres */}
                        <div style={{ position: 'absolute', bottom: 0, width: '100%', display: 'flex', gap: 2, alignItems: 'flex-end', justifyContent: 'center' }}>
                          {/* Barre bleue */}
                          <div style={{ width: '55%', height: hBlue, background: '#2563EB', borderRadius: '3px 3px 0 0', transition: 'height .4s' }} title={`Planifiées: ${s.planifiee}`} />
                          {/* Barre rouge RVNH */}
                          <div style={{ width: '35%', height: hRed, background: '#DC2626', borderRadius: '3px 3px 0 0', transition: 'height .4s' }} title={`RVNH: ${s.rvnh}`} />
                        </div>
                        {/* Nb RVNH label au-dessus barre rouge */}
                        {s.rvnh > 0 && (
                          <div style={{ position: 'absolute', bottom: hRed + 2, right: '6%', fontSize: 10, fontWeight: 800, color: '#DC2626', whiteSpace: 'nowrap' }}>
                            {s.rvnh}
                          </div>
                        )}
                        {/* Taux RVNH label (orange) */}
                        {s.taux_rvnh > 0 && (
                          <div style={{ position: 'absolute', top: tauxToY(s.taux_rvnh) - 14, fontSize: 9, fontWeight: 800, color: '#D97706', whiteSpace: 'nowrap' }}>
                            {s.taux_rvnh}%
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Ligne orange — taux RVNH */}
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                  {series.map((s, i) => {
                    const x = ((i + 0.5) / series.length) * 100;
                    const y = (tauxToY(s.taux_rvnh) / (CHART_H - 24)) * 100;
                    const next = series[i + 1];
                    return (
                      <g key={i}>
                        <circle cx={`${x}%`} cy={`${y}%`} r={4} fill="#D97706" />
                        {next && (
                          <line
                            x1={`${x}%`} y1={`${y}%`}
                            x2={`${((i + 1.5) / series.length) * 100}%`}
                            y2={`${(tauxToY(next.taux_rvnh) / (CHART_H - 24)) * 100}%`}
                            stroke="#D97706" strokeWidth={2} />
                        )}
                      </g>
                    );
                  })}
                </svg>
              </div>

              {/* Labels mois */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 56, display: 'flex' }}>
                {series.map((s, i) => (
                  <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: 'var(--text3)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {s.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Légende */}
            <div style={{ display: 'flex', gap: 18, marginTop: 10, fontSize: 11, flexWrap: 'wrap' }}>
              {[
                { color: '#2563EB', label: 'Nb inter mensuel', type: 'bar' },
                { color: '#DC2626', label: 'Nb de RVNH mensuel', type: 'bar' },
                { color: '#D97706', label: 'Taux de succès objectif cible RVNH', type: 'line' },
              ].map((l, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {l.type === 'bar'  && <div style={{ width: 10, height: 10, background: l.color, borderRadius: 2 }} />}
                  {l.type === 'line' && <div style={{ width: 16, height: 3, background: l.color, borderRadius: 2 }} />}
                  <span style={{ color: 'var(--text3)' }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tableau */}
        {!loading && series.length > 0 && (
          <div style={{ marginTop: 20, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '120px 110px 100px 110px 110px',
              padding: '8px 12px', background: 'var(--bg3)',
              fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .4,
              borderBottom: '1px solid var(--border)',
            }}>
              <div>Mois</div>
              <div style={{ textAlign: 'center' }}>Nb inter</div>
              <div style={{ textAlign: 'center' }}>Nb RVNH</div>
              <div style={{ textAlign: 'center' }}>Taux RVNH</div>
              <div style={{ textAlign: 'center' }}>Taux respect</div>
            </div>
            {series.map((s, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '120px 110px 100px 110px 110px',
                padding: '9px 12px', alignItems: 'center',
                background: i % 2 === 0 ? 'var(--bg2)' : 'transparent',
                borderBottom: i < series.length - 1 ? '1px solid var(--border)' : 'none',
                fontSize: 13,
              }}>
                <div style={{ fontWeight: 600 }}>{s.label}</div>
                <div style={{ textAlign: 'center' }}>
                  <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'planifiee', label: 'Planifiées' }))}
                    style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='planifiee'), background: '#DBEAFE', color: '#1D4ED8' }}>{s.planifiee}</span>
                </div>
                <div style={{ textAlign: 'center' }}>
                  {s.rvnh > 0
                    ? <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'rvnh', label: 'RVNH' }))}
                        style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='rvnh'), background: '#FEE2E2', color: '#991B1B' }}>{s.rvnh}</span>
                    : <span style={{ color: 'var(--text3)' }}>0</span>}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: s.taux_rvnh <= 5 ? '#059669' : '#DC2626' }}>{s.taux_rvnh}%</span>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <span style={{ fontWeight: 800, fontSize: 14, color: s.taux_respect >= objective ? '#059669' : '#DC2626' }}>{s.taux_respect}%</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {drilldown && <DrilldownTable kpi="i004" month={drilldown.month} bucket={drilldown.bucket} label={drilldown.label} onClose={() => setDrilldown(null)} />}
      </div>

      {/* Panel droit */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 200 }}>
        <div className="card" style={{ textAlign: 'center', padding: '24px 28px' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Indicateur I004</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10, lineHeight: 1.4 }}>Taux de respect de l'indicateur :</div>
          <div style={{ fontSize: 42, fontWeight: 900, color: data?.indicator >= objective ? '#059669' : data?.indicator != null ? '#DC2626' : 'var(--text3)', lineHeight: 1 }}>
            {data?.indicator != null ? `${data.indicator}%` : '—'}
          </div>
          {data?.indicator_month && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>{data.indicator_month}</div>}
        </div>

        <div className="card" style={{ textAlign: 'center', padding: '20px 28px' }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Tendance de l'indicateur I004</div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>Tendance sur M-1 :</div>
          <div style={{ fontSize: 36, fontWeight: 900, color: data?.trend >= 0 ? '#059669' : '#DC2626', lineHeight: 1 }}>
            {data?.trend != null ? `${data.trend >= 0 ? '+' : ''}${data.trend}%` : '—'}
          </div>
        </div>

        {totals.planifiee > 0 && (
          <div className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>Période sélectionnée</div>
            {[
              { label: 'Total inter', val: totals.planifiee, color: '#1D4ED8' },
              { label: 'Total RVNH', val: totals.rvnh, color: '#DC2626' },
              { label: 'Taux RVNH', val: `${tauxRvnhGlobal}%`, color: tauxRvnhGlobal <= 5 ? '#059669' : '#DC2626' },
              { label: 'Taux respect', val: `${tauxRespectGlobal}%`, color: tauxRespectGlobal >= objective ? '#059669' : '#DC2626' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: i < 3 ? '1px solid var(--border)' : 'none' }}>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>{s.label}</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: s.color }}>{s.val}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Composant graphique générique 2 barres + ligne (réutilisé par I006 et I007) ─
function DualBarLineChart({
  series, barAKey, barBKey, tauxKey,
  colorA, colorB, colorLine,
  labelA, labelB, labelLine,
  tauxMin = 50, tauxMax = 105, objective = 80,
}) {
  if (!series?.length) return <div style={{ color: 'var(--text3)', fontSize: 13, padding: 20 }}>Aucune donnée</div>;
  const maxBar  = Math.max(...series.map(s => s[barAKey] || 0), 1);
  const CHART_H = 220, BAR_H = 170;
  const tauxToY = (t) => {
    const pct = (Math.min(Math.max(t, tauxMin), tauxMax) - tauxMin) / (tauxMax - tauxMin);
    return CHART_H - 30 - pct * BAR_H;
  };
  const n = series.length;
  return (
    <div>
      <div style={{ position: 'relative', height: CHART_H + 24 }}>
        {/* Axe droit */}
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 24, width: 52, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          {Array.from({ length: 5 }, (_, i) => tauxMax - i * ((tauxMax - tauxMin) / 4)).map(v => (
            <div key={v} style={{ fontSize: 9, color: 'var(--text3)', lineHeight: 1 }}>{Math.round(v)}%</div>
          ))}
        </div>
        {/* Zone barres */}
        <div style={{ position: 'absolute', left: 0, right: 56, top: 0, bottom: 24, background: 'rgba(200,220,255,0.1)', borderRadius: 8 }}>
          {[0,25,50,75,100].map(p => (
            <div key={p} style={{ position:'absolute', top:`${100-p}%`, left:0, right:0, borderTop:'1px solid var(--border)', opacity:.4 }} />
          ))}
          <div style={{ display:'flex', alignItems:'flex-end', height:'100%', gap:6, padding:'0 4px' }}>
            {series.map((s, i) => {
              const hA   = ((s[barAKey]||0) / maxBar) * BAR_H;
              const hB   = Math.max(((s[barBKey]||0) / maxBar) * BAR_H, (s[barBKey]||0) > 0 ? 5 : 0);
              const taux = s[tauxKey];
              return (
                <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', position:'relative', height:'100%' }}>
                  <div style={{ position:'absolute', bottom: hA+3, fontSize:10, fontWeight:700, color:'var(--text2)', whiteSpace:'nowrap' }}>
                    {(s[barAKey]||0) > 0 ? s[barAKey] : ''}
                  </div>
                  <div style={{ position:'absolute', bottom:0, width:'100%', display:'flex', gap:2, alignItems:'flex-end', justifyContent:'center' }}>
                    <div style={{ width:'55%', height:hA, background:colorA, borderRadius:'3px 3px 0 0', transition:'height .4s' }} />
                    <div style={{ width:'35%', height:hB, background:colorB, borderRadius:'3px 3px 0 0', transition:'height .4s' }} />
                  </div>
                  {(s[barBKey]||0) > 0 && (
                    <div style={{ position:'absolute', bottom:hB+2, right:'6%', fontSize:10, fontWeight:800, color:colorB, whiteSpace:'nowrap' }}>
                      {s[barBKey]}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Ligne taux SVG */}
          <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', overflow:'visible', pointerEvents:'none' }}>
            {series.map((s, i) => {
              if (s[tauxKey] == null) return null;
              const x   = ((i+0.5)/n)*100;
              const y   = (tauxToY(s[tauxKey])/(CHART_H-24))*100;
              const col = s[tauxKey] >= objective ? '#059669' : '#DC2626';
              const lbl = (v, c) => <text key={`t${i}_${v.x}`} x={`${v.x}%`} y={`${v.y}%`} dy="-10" textAnchor="middle" fontSize="10" fontWeight="800" fill={c} stroke="white" strokeWidth="3" paintOrder="stroke">{v.val}%</text>;
              const ns  = series[i+1];
              if (!ns?.[tauxKey]) return (
                <g key={i}>
                  <circle cx={`${x}%`} cy={`${y}%`} r={4} fill={colorLine} />
                  {lbl({ x, y, val: s[tauxKey] }, col)}
                </g>
              );
              const nx   = ((i+1.5)/n)*100;
              const ny   = (tauxToY(ns[tauxKey])/(CHART_H-24))*100;
              const colN = ns[tauxKey] >= objective ? '#059669' : '#DC2626';
              return (
                <g key={i}>
                  <line x1={`${x}%`} y1={`${y}%`} x2={`${nx}%`} y2={`${ny}%`} stroke={colorLine} strokeWidth={2.5} />
                  <circle cx={`${x}%`} cy={`${y}%`} r={4} fill={colorLine} />
                  {lbl({ x, y, val: s[tauxKey] }, col)}
                  {i===n-2 && <circle cx={`${nx}%`} cy={`${ny}%`} r={4} fill={colorLine} />}
                  {i===n-2 && lbl({ x: nx, y: ny, val: ns[tauxKey] }, colN)}
                </g>
              );
            })}
          </svg>
        </div>
        {/* Labels mois */}
        <div style={{ position:'absolute', bottom:0, left:0, right:56, display:'flex' }}>
          {series.map((s,i) => (
            <div key={i} style={{ flex:1, textAlign:'center', fontSize:10, color:'var(--text3)', fontWeight:600, whiteSpace:'nowrap' }}>{s.label}</div>
          ))}
        </div>
      </div>
      {/* Légende */}
      <div style={{ display:'flex', gap:16, marginTop:10, fontSize:11, flexWrap:'wrap' }}>
        {[{color:colorA,label:labelA,type:'bar'},{color:colorB,label:labelB,type:'bar'},{color:colorLine,label:labelLine,type:'line'}].map((l,i)=>(
          <div key={i} style={{ display:'flex', alignItems:'center', gap:5 }}>
            {l.type==='bar'  && <div style={{ width:12, height:12, background:l.color, borderRadius:2 }} />}
            {l.type==='line' && <div style={{ width:20, height:3,  background:l.color, borderRadius:2 }} />}
            <span style={{ color:'var(--text2)' }}>{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Panneau droit KPI générique ───────────────────────────────────────────────
function KpiRightPanel({ label, indicator, indicatorMonth, trend, objective = 80, children }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14, minWidth:200 }}>
      <div className="card" style={{ textAlign:'center' }}>
        <div style={{ fontSize:11, color:'var(--text3)', marginBottom:4 }}>{label}</div>
        <div style={{ fontSize:11, fontWeight:600, color:'var(--text2)', marginBottom:8 }}>Taux de respect de l'indicateur</div>
        <div style={{ fontSize:38, fontWeight:800, lineHeight:1.1, color:(indicator??0)>=objective?'#059669':'#DC2626' }}>
          {indicator != null ? `${indicator}%` : '—'}
        </div>
        <div style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>{indicatorMonth||''}</div>
      </div>
      {trend != null && (
        <div className="card" style={{ textAlign:'center' }}>
          <div style={{ fontSize:11, color:'var(--text3)', marginBottom:6 }}>Tendance sur M-1</div>
          <div style={{ fontSize:26, fontWeight:800, color:trend>=0?'#059669':'#DC2626' }}>
            {trend>=0?`+${trend}%`:`${trend}%`}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

// ── I006 : Transmission rapide des devis ─────────────────────────────────────
function I006Panel() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [available, setAvailable] = useState([]);
  // Le chargement initial fixe la selection par defaut ; sans ce drapeau, la
  // closure figee du useCallback la reappliquait a chaque changement de mois.
  const initFait = useRef(false);
  const [selMonths, setSelMonths] = useState([]);
  const [drilldown, setDrilldown] = useState(null);

  const load = useCallback(async (months) => {
    setLoading(true);
    try {
      const params = months?.length ? { months: months.join(',') } : {};
      const res = await api.get('/admin/kpi/i006', { params });
      setData(res.data);
      if (res.data.availableMonths?.length && !initFait.current) {
        initFait.current = true;
        const sorted = res.data.availableMonths.slice().sort();
        setAvailable(sorted);
        const last6 = sorted.slice(-6);
        setSelMonths(last6);
        const r2 = await api.get('/admin/kpi/i006', { params: { months: last6.join(',') } });
        setData(r2.data);
      }
    } catch (e) { setData(null); signalerErreur(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load([]); }, []); // eslint-disable-line
  const handleMonthChange = (months) => { setSelMonths(months); load(months); };
  const series = data?.series || [];
  const totals = series.reduce((a,s) => ({ total: a.total+s.total, late: a.late+s.late }), { total:0, late:0 });
  const tauxGlobal = totals.total > 0 ? Math.round(((totals.total-totals.late)/totals.total)*10000)/100 : 0;

  return (
    <div style={{ display:'flex', gap:20, alignItems:'flex-start', flexWrap:'wrap' }}>
      <div className="card" style={{ flex:1, minWidth:500 }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:4, color:'var(--text)' }}>
          Indicateur I006 / Transmission rapide des devis et fiches DOSTEC
        </div>
        <div style={{ fontSize:11, color:'var(--text3)', marginBottom:14 }}>Devis/DOSTEC soumis dans les délais J+2 calendaires</div>
        <div style={{ marginBottom:14 }}>
          <MonthRangeSelector available={available} selected={selMonths} onChange={handleMonthChange} />
        </div>
        {loading ? (
          <div style={{ textAlign:'center', padding:30, color:'var(--text3)' }}>Chargement…</div>
        ) : !series.length ? (
          <div style={{ color:'var(--text3)', fontSize:13, padding:20 }}>Aucune donnée</div>
        ) : (
          <DualBarLineChart
            series={series}
            barAKey="total" barBKey="late" tauxKey="taux_respect"
            colorA="#60A5FA" colorB="#374151" colorLine="#2563EB"
            labelA="Nb devis/DOSTEC transmis" labelB="Transmis après J+2" labelLine="Taux de respect"
            tauxMin={80} tauxMax={105} objective={95}
          />
        )}

        {/* Tableau détaillé I006 */}
        {!loading && series.length > 0 && (
          <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display:'grid', gridTemplateColumns:'110px 90px 110px 130px 100px', padding:'8px 12px', background:'var(--bg3)', fontSize:10, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:.4, borderBottom:'1px solid var(--border)' }}>
              <div>Mois</div><div style={{textAlign:'center'}}>Total docs</div><div style={{textAlign:'center'}}>En retard</div><div style={{textAlign:'center'}}>Dans les délais</div><div style={{textAlign:'center'}}>Taux</div>
            </div>
            {series.map((s, i) => {
              const onTime = s.total - s.late;
              return (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'110px 90px 110px 130px 100px', padding:'9px 12px', alignItems:'center', background:i%2===0?'var(--bg2)':'transparent', borderBottom:i<series.length-1?'1px solid var(--border)':'none', fontSize:13 }}>
                  <div style={{fontWeight:600}}>{s.label}</div>
                  <div style={{textAlign:'center', fontWeight:700}}>{s.total}</div>
                  <div style={{textAlign:'center'}}>
                    {s.late > 0
                      ? <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'late', label: 'Docs en retard (>J+2)' }))}
                          style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='late'), background:'#FEE2E2', color:'#991B1B' }}>{s.late}</span>
                      : <span style={{color:'var(--text3)'}}>0</span>}
                  </div>
                  <div style={{textAlign:'center'}}>
                    <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'on_time', label: 'Docs dans les délais' }))}
                      style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='on_time'), background:'#D1FAE5', color:'#065F46' }}>{onTime}</span>
                  </div>
                  <div style={{textAlign:'center'}}>
                    <span style={{fontWeight:800, fontSize:14, color:s.taux_respect>=95?'#059669':'#DC2626'}}>{s.taux_respect}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {drilldown && <DrilldownTable kpi="i006" month={drilldown.month} bucket={drilldown.bucket} label={drilldown.label} onClose={() => setDrilldown(null)} />}
      </div>
      <KpiRightPanel label="Indicateur I006" indicator={data?.indicator} indicatorMonth={data?.indicator_month} trend={data?.trend} objective={95}>
        {series.length > 0 && (
          <div className="card" style={{ fontSize:12 }}>
            <div style={{ fontWeight:600, marginBottom:8, color:'var(--text)' }}>Récap période</div>
            <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:'var(--text3)' }}>Total devis/DOSTEC</span>
                <span style={{ fontWeight:700 }}>{totals.total}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:'var(--text3)' }}>Transmis en retard</span>
                <span style={{ fontWeight:700, color:'#DC2626' }}>{totals.late}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:'var(--text3)' }}>Taux global</span>
                <span style={{ fontWeight:700, color:tauxGlobal>=95?'#059669':'#DC2626' }}>{tauxGlobal}%</span>
              </div>
            </div>
          </div>
        )}
      </KpiRightPanel>
    </div>
  );
}

// ── I007 : Taux de réussite à la première intervention ───────────────────────
// Répartition des causes d'échec, agrégée sur la période sélectionnée.
// L'imputation est l'argument central face à Covage : elle montre quelle part
// des échecs relève réellement de VALEA.
const COULEUR_IMPUTATION = {
  'VALEA':                   '#DC2626',
  'Client':                  '#2563EB',
  'Site / accès':            '#7C3AED',
  'Infrastructure':          '#0891B2',
  'Réseau Covage':           '#0D9488',
  'Données Covage':          '#CA8A04',
  'Opérateur tiers':         '#DB2777',
  'Travaux supplémentaires': '#65A30D',
  'Indéterminé':             '#6B7280',
};

function CausesEchec({ series }) {
  const [sortie, setSortie] = React.useState(null);

  /**
   * La periode couverte, deduite des mois affiches.
   *
   * Le bloc agrege les mois selectionnes : le classeur doit porter la meme
   * periode, sinon on envoie a Covage une liste qui ne correspond pas au
   * chiffre qu'on vient de lire a l'ecran.
   */
  const periode = React.useMemo(() => {
    const cles = series.map(s => s.key).filter(Boolean).sort();
    if (!cles.length) return null;
    const [a, m] = cles[cles.length - 1].split('-').map(Number);
    // Le jour 0 du mois suivant, en heure locale : voir services/dates.js.
    const f = new Date(a, m, 0);
    const jour = d => d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0')
      + '-' + String(d.getDate()).padStart(2, '0');
    return { debut: cles[0] + '-01', fin: jour(f) };
  }, [series]);

  /**
   * Le releve d'un motif, en classeur.
   *
   * Il sert a refacturer le deplacement : le serveur y joint l'heure
   * d'arrivee du technicien, le numero de decharge, les photos prises sur
   * place, la confirmation envoyee au client et les appels restes sans
   * reponse. Voir services/echecsDeplacements.js.
   */
  const exporter = async (motif) => {
    if (!periode) return;
    setSortie(motif);
    try {
      const r = await api.get('/admin/kpi/deplacements.xlsx', {
        responseType: 'blob',
        params: { debut: periode.debut, fin: periode.fin, motif },
      });
      const trouve = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '');
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = trouve ? trouve[1] : 'deplacements.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // En « responseType: blob », l'erreur du serveur arrive en blob aussi.
      let m = e.message;
      try {
        if (e.response?.data instanceof Blob) {
          m = JSON.parse(await e.response.data.text()).error || m;
        }
      } catch { /* pas du JSON : on garde le message d'axios */ }
      alert('Export impossible : ' + m);
    } finally {
      setSortie(null);
    }
  };

  const cumul = (cle) => {
    const t = {}, imp = {};
    series.forEach(s => (s[cle] || []).forEach(x => {
      t[x.nom] = (t[x.nom] || 0) + x.n;
      if (x.imputation) imp[x.nom] = x.imputation;
    }));
    return Object.entries(t).sort((a, b) => b[1] - a[1])
      .map(([nom, n]) => ({ nom, n, imputation: imp[nom] }));
  };
  const imputations = cumul('imputations');
  const motifs      = cumul('motifs');
  const total       = imputations.reduce((a, x) => a + x.n, 0);
  if (!total) return null;

  const partValea = imputations.find(x => x.nom === 'VALEA')?.n || 0;

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2, color: 'var(--text)' }}>
        Causes des {total} échecs de la période
      </div>
      <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
        {partValea === 0
          ? 'Aucun échec imputable à VALEA sur la période.'
          : `${partValea} échec${partValea > 1 ? 's' : ''} imputable${partValea > 1 ? 's' : ''} à VALEA, soit ${(partValea / total * 100).toFixed(1)} % du total.`}
      </div>

      {/* Barre d'imputation */}
      <div style={{ display: 'flex', height: 26, borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
        {imputations.map(({ nom, n }) => (
          <div key={nom} title={`${nom} : ${n}`}
            style={{ width: `${n / total * 100}%`, background: COULEUR_IMPUTATION[nom] || '#9CA3AF',
                     display: 'flex', alignItems: 'center', justifyContent: 'center',
                     fontSize: 10, fontWeight: 700, color: '#fff' }}>
            {n / total > 0.07 ? n : ''}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginBottom: 14 }}>
        {imputations.map(({ nom, n }) => (
          <div key={nom} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text2)' }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: COULEUR_IMPUTATION[nom] || '#9CA3AF' }} />
            {nom} <strong>{n}</strong>
            <span style={{ color: 'var(--text3)' }}>({(n / total * 100).toFixed(1)} %)</span>
          </div>
        ))}
      </div>

      {/* Motifs détaillés */}
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '7px 12px', background: 'var(--bg3)', fontSize: 10, fontWeight: 700,
                      color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .4 }}>
          Motif détaillé
        </div>
        {motifs.map(({ nom, n, imputation }, i) => (
          <div key={nom} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px',
                                  background: i % 2 === 0 ? 'var(--bg2)' : 'transparent', fontSize: 12 }}>
            <span title={imputation} style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0,
                           background: COULEUR_IMPUTATION[imputation] || '#9CA3AF' }} />
            <span style={{ flex: 1, color: 'var(--text2)' }}>{nom}</span>
            <span style={{ flex: '0 0 120px', background: 'var(--bg3)', borderRadius: 3, height: 6, overflow: 'hidden' }}>
              <span style={{ display: 'block', height: '100%', width: `${n / motifs[0].n * 100}%`, background: 'var(--blue2)' }} />
            </span>
            <strong style={{ width: 34, textAlign: 'right' }}>{n}</strong>
            <span style={{ width: 48, textAlign: 'right', color: 'var(--text3)', fontSize: 11 }}>
              {(n / total * 100).toFixed(1)} %
            </span>
            {/* Le releve a envoyer a Covage : chaque ligne porte de quoi
                etablir que le rendez-vous etait pris, que le client en etait
                averti et que le technicien est venu. */}
            <button type="button" onClick={() => exporter(nom)} disabled={sortie === nom}
              title={'Telecharger le releve des ' + n + ' « ' + nom + ' » de la periode, '
                + 'avec les preuves du deplacement, pour le refacturer a Covage'}
              style={{ flexShrink: 0, border: '1px solid var(--border)', background: 'var(--bg3)',
                       color: 'var(--text2)', borderRadius: 6, padding: '1px 7px', fontSize: 10,
                       fontWeight: 700, cursor: sortie === nom ? 'wait' : 'pointer',
                       whiteSpace: 'nowrap' }}>
              {sortie === nom ? '…' : 'Excel'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function I007Panel() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [available, setAvailable] = useState([]);
  // Le chargement initial fixe la selection par defaut ; sans ce drapeau, la
  // closure figee du useCallback la reappliquait a chaque changement de mois.
  const initFait = useRef(false);
  const [selMonths, setSelMonths] = useState([]);
  const [drilldown, setDrilldown] = useState(null);

  const load = useCallback(async (months) => {
    setLoading(true);
    try {
      const params = months?.length ? { months: months.join(',') } : {};
      const res = await api.get('/admin/kpi/i007', { params });
      setData(res.data);
      if (res.data.availableMonths?.length && !initFait.current) {
        initFait.current = true;
        const sorted = res.data.availableMonths.slice().sort();
        setAvailable(sorted);
        const last6 = sorted.slice(-6);
        setSelMonths(last6);
        const r2 = await api.get('/admin/kpi/i007', { params: { months: last6.join(',') } });
        setData(r2.data);
      }
    } catch (e) { setData(null); signalerErreur(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load([]); }, []); // eslint-disable-line
  const handleMonthChange = (months) => { setSelMonths(months); load(months); };
  const series = data?.series || [];
  const totals = series.reduce((a,s) => ({ total: a.total+s.total, one_shot: a.one_shot+s.one_shot }), { total:0, one_shot:0 });
  const tauxGlobal = totals.total > 0 ? Math.round((totals.one_shot/totals.total)*10000)/100 : 0;

  return (
    <div style={{ display:'flex', gap:20, alignItems:'flex-start', flexWrap:'wrap' }}>
      <div className="card" style={{ flex:1, minWidth:500 }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:4, color:'var(--text)' }}>
          Indicateur I007 / Taux de réussite à la première intervention
        </div>
        <div style={{ fontSize:11, color:'var(--text3)', marginBottom:14 }}>CLEARED sans FAILED antérieur sur le même dossier</div>
        <div style={{ marginBottom:14 }}>
          <MonthRangeSelector available={available} selected={selMonths} onChange={handleMonthChange} />
        </div>
        {loading ? (
          <div style={{ textAlign:'center', padding:30, color:'var(--text3)' }}>Chargement…</div>
        ) : !series.length ? (
          <div style={{ color:'var(--text3)', fontSize:13, padding:20 }}>Aucune donnée</div>
        ) : (
          <DualBarLineChart
            series={series}
            barAKey="total" barBKey="one_shot" tauxKey="taux"
            colorA="#F59E0B" colorB="#16A34A" colorLine="#B45309"
            labelA="Nb inter mensuel" labelB="Cleared ONE SHOT" labelLine="Taux réussite 1er RDV"
            tauxMin={50} tauxMax={100} objective={85}
          />
        )}

        {/* Tableau détaillé I007 */}
        {!loading && series.length > 0 && (
          <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display:'grid', gridTemplateColumns:'110px 80px 120px 120px 100px', padding:'8px 12px', background:'var(--bg3)', fontSize:10, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:.4, borderBottom:'1px solid var(--border)' }}>
              <div>Mois</div><div style={{textAlign:'center'}}>Total CRIs</div><div style={{textAlign:'center'}}>ONE SHOT</div><div style={{textAlign:'center'}}>FAILED</div><div style={{textAlign:'center'}}>Taux</div>
            </div>
            {series.map((s, i) => {
              const failed = s.total - s.one_shot;
              return (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'110px 80px 120px 120px 100px', padding:'9px 12px', alignItems:'center', background:i%2===0?'var(--bg2)':'transparent', borderBottom:i<series.length-1?'1px solid var(--border)':'none', fontSize:13 }}>
                  <div style={{fontWeight:600}}>{s.label}</div>
                  <div style={{textAlign:'center', fontWeight:700}}>{s.total}</div>
                  <div style={{textAlign:'center'}}>
                    <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'one_shot', label: 'ONE SHOT (Cleared sans Failed)' }))}
                      style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='one_shot'), background:'#D1FAE5', color:'#065F46' }}>{s.one_shot}</span>
                  </div>
                  <div style={{textAlign:'center'}}>
                    {failed > 0
                      ? <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'failed', label: 'FAILED' }))}
                          style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='failed'), background:'#FEE2E2', color:'#991B1B' }}>{failed}</span>
                      : <span style={{color:'var(--text3)'}}>0</span>}
                  </div>
                  <div style={{textAlign:'center'}}>
                    <span style={{fontWeight:800, fontSize:14, color:s.taux>=85?'#059669':'#DC2626'}}>{s.taux}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!loading && series.length > 0 && <CausesEchec series={series} />}

        {drilldown && <DrilldownTable kpi="i007" month={drilldown.month} bucket={drilldown.bucket} label={drilldown.label} onClose={() => setDrilldown(null)} />}
      </div>
      <KpiRightPanel label="Indicateur I007" indicator={data?.indicator} indicatorMonth={data?.indicator_month} trend={data?.trend} objective={85}>
        {series.length > 0 && (
          <div className="card" style={{ fontSize:12 }}>
            <div style={{ fontWeight:600, marginBottom:8, color:'var(--text)' }}>Récap période</div>
            <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:'var(--text3)' }}>Total inter (CRIs)</span>
                <span style={{ fontWeight:700 }}>{totals.total}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:'var(--text3)' }}>Cleared ONE SHOT</span>
                <span style={{ fontWeight:700, color:'#16A34A' }}>{totals.one_shot}</span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between' }}>
                <span style={{ color:'var(--text3)' }}>Taux global</span>
                <span style={{ fontWeight:700, color:tauxGlobal>=85?'#059669':'#DC2626' }}>{tauxGlobal}%</span>
              </div>
            </div>
          </div>
        )}
      </KpiRightPanel>
    </div>
  );
}

// ── I005 : Délai d'émission des PV/CRI ───────────────────────────────────────
function I005Panel() {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [available, setAvailable] = useState([]);
  // Le chargement initial fixe la selection par defaut ; sans ce drapeau, la
  // closure figee du useCallback la reappliquait a chaque changement de mois.
  const initFait = useRef(false);
  const [selMonths, setSelMonths] = useState([]);
  const [drilldown, setDrilldown] = useState(null);

  const load = useCallback(async (months) => {
    setLoading(true);
    try {
      const params = months?.length ? { months: months.join(',') } : {};
      const res = await api.get('/admin/kpi/i005', { params });
      setData(res.data);
      if (res.data.availableMonths?.length && !initFait.current) {
        initFait.current = true;
        const sorted = res.data.availableMonths.slice().sort();
        setAvailable(sorted);
        const last6 = sorted.slice(-6);
        setSelMonths(last6);
        const r2 = await api.get('/admin/kpi/i005', { params: { months: last6.join(',') } });
        setData(r2.data);
      }
    } catch (e) { setData(null); signalerErreur(e); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line

  useEffect(() => { load([]); }, []); // eslint-disable-line

  const handleMonthChange = (months) => { setSelMonths(months); load(months); };
  const series    = data?.series || [];
  const objective = 95;

  const totals = series.reduce((acc, s) => ({
    total: acc.total + s.total,
    late:  acc.late  + s.late,
  }), { total: 0, late: 0 });
  const tauxRespectGlobal = totals.total > 0
    ? Math.round(((totals.total - totals.late) / totals.total) * 10000) / 100
    : 0;

  const maxBar  = Math.max(...series.map(s => s.total), 1);
  const CHART_H = 220;
  const BAR_H   = 170;

  // Ligne verte : taux_respect, axe droit 90-110%
  const TAUX_MIN = 90, TAUX_MAX = 110;
  const tauxToY = (t) => {
    const pct = (Math.min(Math.max(t, TAUX_MIN), TAUX_MAX) - TAUX_MIN) / (TAUX_MAX - TAUX_MIN);
    return CHART_H - 30 - pct * BAR_H;
  };

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      {/* Graphique principal */}
      <div className="card" style={{ flex: 1, minWidth: 500 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: 'var(--text)' }}>
          Indicateur I005 / Délai d'émission des PV/CRI
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14 }}>Nb interventions/mois — CRI soumis dans le délai J+1 ouvrable</div>

        <div style={{ marginBottom: 14 }}>
          <MonthRangeSelector available={available} selected={selMonths} onChange={handleMonthChange} />
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text3)' }}>Chargement…</div>
        ) : !series.length ? (
          <div style={{ color: 'var(--text3)', fontSize: 13, padding: 20 }}>Aucune donnée</div>
        ) : (
          <div>
            <div style={{ position: 'relative', height: CHART_H + 24 }}>
              {/* Axe droit */}
              <div style={{ position: 'absolute', right: 0, top: 0, bottom: 24, width: 52, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                {[110, 105, 100, 95, 90].map(v => (
                  <div key={v} style={{ fontSize: 9, color: 'var(--text3)', lineHeight: 1 }}>{v}%</div>
                ))}
              </div>

              {/* Zone barres */}
              <div style={{ position: 'absolute', left: 0, right: 56, top: 0, bottom: 24, background: 'rgba(255,220,180,0.12)', borderRadius: 8 }}>
                {[0, 25, 50, 75, 100].map(p => (
                  <div key={p} style={{ position: 'absolute', top: `${100 - p}%`, left: 0, right: 0, borderTop: '1px solid var(--border)', opacity: .4 }} />
                ))}

                <div style={{ display: 'flex', alignItems: 'flex-end', height: '100%', gap: 6, padding: '0 4px' }}>
                  {series.map((s, i) => {
                    const hTotal = (s.total / maxBar) * BAR_H;
                    const hLate  = Math.max((s.late  / maxBar) * BAR_H, s.late > 0 ? 6 : 0);
                    const taux   = s.taux_respect;
                    return (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', height: '100%' }}>
                        {/* Nb total inter */}
                        <div style={{ position: 'absolute', bottom: hTotal + 3, fontSize: 10, fontWeight: 700, color: '#B45309', whiteSpace: 'nowrap' }}>
                          {s.total > 0 ? s.total : ''}
                        </div>
                        {/* Barres */}
                        <div style={{ position: 'absolute', bottom: 0, width: '100%', display: 'flex', gap: 2, alignItems: 'flex-end', justifyContent: 'center' }}>
                          {/* Barre jaune — total inter */}
                          <div style={{ width: '55%', height: hTotal, background: '#F59E0B', borderRadius: '3px 3px 0 0', transition: 'height .4s' }} title={`Total inter: ${s.total}`} />
                          {/* Barre vert foncé — CRI en retard */}
                          <div style={{ width: '35%', height: hLate, background: '#065F46', borderRadius: '3px 3px 0 0', transition: 'height .4s' }} title={`CRI en retard: ${s.late}`} />
                        </div>
                        {/* Nb late label */}
                        {s.late > 0 && (
                          <div style={{ position: 'absolute', bottom: hLate + 2, right: '6%', fontSize: 10, fontWeight: 800, color: '#065F46', whiteSpace: 'nowrap' }}>
                            {s.late}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Ligne verte — taux respect */}
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                  {series.map((s, i) => {
                    if (s.taux_respect == null) return null;
                    const n    = series.length;
                    const x    = ((i + 0.5) / n) * 100;
                    const y    = (tauxToY(s.taux_respect) / (CHART_H - 24)) * 100;
                    const col  = s.taux_respect >= objective ? '#059669' : '#DC2626';
                    const lbl  = (px, py, val, c) => <text x={`${px}%`} y={`${py}%`} dy="-10" textAnchor="middle" fontSize="10" fontWeight="800" fill={c} stroke="white" strokeWidth="3" paintOrder="stroke">{val}%</text>;
                    const next = series[i + 1];
                    if (!next?.taux_respect) return (
                      <g key={i}>
                        <circle cx={`${x}%`} cy={`${y}%`} r={4} fill="#16A34A" />
                        {lbl(x, y, s.taux_respect, col)}
                      </g>
                    );
                    const nx   = ((i + 1.5) / n) * 100;
                    const ny   = (tauxToY(next.taux_respect) / (CHART_H - 24)) * 100;
                    const colN = next.taux_respect >= objective ? '#059669' : '#DC2626';
                    return (
                      <g key={i}>
                        <line x1={`${x}%`} y1={`${y}%`} x2={`${nx}%`} y2={`${ny}%`} stroke="#16A34A" strokeWidth={2.5} />
                        <circle cx={`${x}%`} cy={`${y}%`} r={4} fill="#16A34A" />
                        {lbl(x, y, s.taux_respect, col)}
                        {i === n - 2 && <circle cx={`${nx}%`} cy={`${ny}%`} r={4} fill="#16A34A" />}
                        {i === n - 2 && lbl(nx, ny, next.taux_respect, colN)}
                      </g>
                    );
                  })}
                </svg>
              </div>

              {/* Labels mois */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 56, display: 'flex' }}>
                {series.map((s, i) => (
                  <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: 'var(--text3)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {s.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Légende */}
            <div style={{ display: 'flex', gap: 18, marginTop: 10, fontSize: 11, flexWrap: 'wrap' }}>
              {[
                { color: '#F59E0B', label: 'Nb inter mensuel effectuées', type: 'bar' },
                { color: '#065F46', label: 'Délai CRI > 1 jour ouvrable',  type: 'bar' },
                { color: '#16A34A', label: 'Taux de respect',               type: 'line' },
              ].map((l, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  {l.type === 'bar'  && <div style={{ width: 12, height: 12, background: l.color, borderRadius: 2 }} />}
                  {l.type === 'line' && <div style={{ width: 20, height: 3,  background: l.color, borderRadius: 2 }} />}
                  <span style={{ color: 'var(--text2)' }}>{l.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tableau détaillé I005 */}
        {!loading && series.length > 0 && (
          <div style={{ marginTop: 16, border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
            <div style={{ display:'grid', gridTemplateColumns:'110px 80px 100px 120px 100px', padding:'8px 12px', background:'var(--bg3)', fontSize:10, fontWeight:700, color:'var(--text3)', textTransform:'uppercase', letterSpacing:.4, borderBottom:'1px solid var(--border)' }}>
              <div>Mois</div><div style={{textAlign:'center'}}>Total</div><div style={{textAlign:'center'}}>En retard</div><div style={{textAlign:'center'}}>Dans les délais</div><div style={{textAlign:'center'}}>Taux</div>
            </div>
            {series.map((s, i) => {
              const onTime = s.total - s.late;
              return (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'110px 80px 100px 120px 100px', padding:'9px 12px', alignItems:'center', background:i%2===0?'var(--bg2)':'transparent', borderBottom:i<series.length-1?'1px solid var(--border)':'none', fontSize:13 }}>
                  <div style={{fontWeight:600}}>{s.label}</div>
                  <div style={{textAlign:'center', fontWeight:700}}>{s.total}</div>
                  <div style={{textAlign:'center'}}>
                    {s.late > 0
                      ? <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'late', label: 'CRI en retard' }))}
                          style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='late'), background:'#FEE2E2', color:'#991B1B' }}>{s.late}</span>
                      : <span style={{color:'var(--text3)'}}>0</span>}
                  </div>
                  <div style={{textAlign:'center'}}>
                    <span onClick={() => setDrilldown(d => toggleDD(d, { month: s.key, bucket: 'on_time', label: 'CRI dans les délais' }))}
                      style={{ ...clickableCell(drilldown?.month===s.key && drilldown?.bucket==='on_time'), background:'#D1FAE5', color:'#065F46' }}>{onTime}</span>
                  </div>
                  <div style={{textAlign:'center'}}>
                    <span style={{fontWeight:800, fontSize:14, color:s.taux_respect>=objective?'#059669':'#DC2626'}}>{s.taux_respect}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {drilldown && <DrilldownTable kpi="i005" month={drilldown.month} bucket={drilldown.bucket} label={drilldown.label} onClose={() => setDrilldown(null)} />}
      </div>

      {/* Panel droit — indicateur */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 200 }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Indicateur I005</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text2)', marginBottom: 8 }}>
            Taux de respect de l'indicateur
          </div>
          <div style={{
            fontSize: 38, fontWeight: 800, lineHeight: 1.1,
            color: (data?.indicator ?? 0) >= objective ? '#059669' : '#DC2626',
          }}>
            {data?.indicator != null ? `${data.indicator}%` : '—'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>{data?.indicator_month || ''}</div>
        </div>

        {data?.trend != null && (
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Tendance sur M-1</div>
            <div style={{
              fontSize: 26, fontWeight: 800,
              color: data.trend >= 0 ? '#059669' : '#DC2626',
            }}>
              {data.trend >= 0 ? `+${data.trend}%` : `${data.trend}%`}
            </div>
          </div>
        )}

        {/* Récap période */}
        {series.length > 0 && (
          <div className="card" style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>Récap période</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text3)' }}>Total inter</span>
                <span style={{ fontWeight: 700 }}>{totals.total}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text3)' }}>CRI en retard</span>
                <span style={{ fontWeight: 700, color: '#DC2626' }}>{totals.late}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text3)' }}>Taux global</span>
                <span style={{ fontWeight: 700, color: tauxRespectGlobal >= objective ? '#059669' : '#DC2626' }}>
                  {tauxRespectGlobal}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────

// ─── Grille SLA ──────────────────────────────────────────────────────────────
//
// La vue d'entrée : les vingt engagements contractuels de Covage, groupés par
// étape, avec le seuil et la valeur du mois. Les lignes non encore calculées y
// figurent aussi — les masquer donnerait l'illusion d'une grille complète.

/**
 * Les quatre niveaux que Covage lit sur la restitution, et le palier de bonus
 * qui reste le nôtre. Le serveur pose la couleur sur chaque ligne — même règle
 * que le classeur et la page publiée, voir services/slaCouleur.js — et le
 * panel se contente de la peindre : trois affichages, un seul verdict.
 */
const COULEUR_STYLE = {
  bonus:  { libelle: 'Palier bonus', bg: 'rgba(21,128,61,0.16)',  fg: '#15803d' },
  vert:   { libelle: 'Maîtrisé',     bg: 'rgba(21,128,61,0.10)',  fg: '#15803d' },
  jaune:  { libelle: 'Vigilance',    bg: 'rgba(161,140,0,0.14)',  fg: '#8a7500' },
  orange: { libelle: 'À améliorer',  bg: 'rgba(154,74,8,0.12)',   fg: '#9a4a08' },
  rouge:  { libelle: 'Critique',     bg: 'rgba(185,28,28,0.10)',  fg: '#b91c1c' },
  faible: { libelle: 'Non significatif', bg: 'rgba(139,146,165,0.10)', fg: '#5b6672' },
  gris:   { libelle: 'Non mesuré',   bg: 'rgba(139,146,165,0.12)', fg: '#6b7280' },
};

// Les lignes sans couleur — indicateur pas encore branché, mesure sans seuil —
// gardent leur ancien mot : elles ne relèvent d'aucun des quatre niveaux.
const STATUT_STYLE = {
  sans_donnee: { libelle: 'Aucune donnée', bg: 'rgba(139,146,165,0.12)', fg: '#6b7280' },
  a_venir:     { libelle: 'À calculer',   bg: 'rgba(180,83,9,0.10)',   fg: '#b45309' },
  mesure:      { libelle: 'Mesuré',       bg: 'rgba(3,105,161,0.10)',  fg: '#0369a1' },
};

/** La pastille d'une ligne : sa couleur si le serveur en donne une. */
function styleDeLigne(l) {
  if (l.couleur && COULEUR_STYLE[l.couleur] && l.statut !== 'mesure') return COULEUR_STYLE[l.couleur];
  return STATUT_STYLE[l.statut] || STATUT_STYLE.a_venir;
}

function GrilleSLA({ onOuvrir }) {
  const [data, setData] = useState(null);
  const [mois, setMois] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [sortie, setSortie] = useState(false);

  /**
   * La restitution du mois, en classeur.
   *
   * Les vingt engagements, le taux du mois et celui du mois précédent, une
   * couleur par ligne, les conventions de calcul arrêtées avec Covage, et un
   * onglet de dossiers par indicateur manqué. C'est le document qu'on envoie :
   * un taux sans sa liste ne se discute pas.
   */
  const exporter = async () => {
    if (!mois) return;
    setSortie(true);
    try {
      const r = await api.get('/admin/kpi/sla.xlsx', { responseType: 'blob', params: { mois } });
      const trouve = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '');
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = trouve ? trouve[1] : 'SLA_Covage_' + mois + '.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // En « responseType: blob », l'erreur du serveur arrive en blob aussi.
      let m = e.message;
      try {
        if (e.response?.data instanceof Blob) {
          m = JSON.parse(await e.response.data.text()).error || m;
        }
      } catch { /* pas du JSON : on garde le message d'axios */ }
      alert('Export impossible : ' + m);
    } finally { setSortie(false); }
  };

  useEffect(() => {
    setChargement(true);
    api.get('/admin/kpi/sla', { params: mois ? { mois } : { mois: moisParDefaut() } })
      .then(r => { setData(r.data); if (!mois) setMois(r.data.mois); })
      .catch(e => signalerErreur(e))
      .finally(() => setChargement(false));
  }, [mois]);

  if (chargement && !data) return <div style={{ padding: 40, color: 'var(--text3)' }}>Chargement…</div>;
  if (!data) return null;

  const etapes = [];
  data.lignes.forEach(l => {
    const cle = l.perimetre + ' — ' + l.etape;
    let g = etapes.find(x => x.cle === cle);
    if (!g) { g = { cle, perimetre: l.perimetre, etape: l.etape, lignes: [] }; etapes.push(g); }
    g.lignes.push(l);
  });

  const calculees = data.lignes.filter(l => l.source).length;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <select value={mois || ''} onChange={e => setMois(e.target.value)} style={{
          padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border)',
          background: 'var(--bg2)', color: 'var(--text)', fontSize: 13, fontWeight: 600,
        }}>
          {(data.availableMonths || []).map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>
          {calculees} engagement{calculees > 1 ? 's' : ''} sur {data.lignes.length} sont calculés à ce jour
        </span>
        {/* Le document mensuel pour Covage : la grille, le mois précédent en
            regard, les méthodes de calcul et les dossiers en cause. */}
        <button type="button" onClick={exporter} disabled={sortie}
          title={'Télécharger la restitution du mois : les 20 engagements, le mois précédent en '
            + 'regard, les conventions de calcul et un onglet par indicateur manqué'}
          style={{ marginLeft: 'auto', border: '1px solid var(--border)', background: 'var(--bg3)',
                   color: 'var(--text2)', borderRadius: 7, padding: '6px 12px', fontSize: 12,
                   fontWeight: 700, cursor: sortie ? 'wait' : 'pointer' }}>
          {sortie ? 'Préparation…' : 'Restitution Excel'}
        </button>
      </div>

      {etapes.map(g => (
        <div key={g.cle} className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
              background: g.perimetre === 'VT' ? 'rgba(124,58,237,0.12)' : 'rgba(29,78,216,0.12)',
              color: g.perimetre === 'VT' ? '#7c3aed' : '#1d4ed8',
            }}>{g.perimetre === 'VT' ? 'Visite technique' : 'Production'}</span>
            <span style={{ fontWeight: 700, fontSize: 14 }}>{g.etape}</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg3)' }}>
                  {['Engagement', 'SLA', 'Bonus', 'Réalisé', 'Volume', 'Statut'].map(h => (
                    <th key={h} style={{
                      padding: '7px 12px', textAlign: h === 'Engagement' ? 'left' : 'right',
                      fontWeight: 700, fontSize: 10, color: 'var(--text3)',
                      textTransform: 'uppercase', letterSpacing: .4,
                      borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {g.lignes.map((l, i) => {
                  const st = styleDeLigne(l);
                  // Seuls les indicateurs dotés d'un onglet détaillé s'ouvrent ;
                  // les autres n'auraient rien à montrer.
                  const cliquable = !!l.source && /^i00\d$/.test(l.source) && !!onOuvrir;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '9px 12px' }}>
                        <div
                          onClick={cliquable ? () => onOuvrir(l.source, l.perimetre) : undefined}
                          style={{
                            color: cliquable ? 'var(--blue2)' : 'var(--text)',
                            cursor: cliquable ? 'pointer' : 'default',
                            fontWeight: cliquable ? 600 : 400,
                          }}>
                          {l.libelle}
                        </div>
                        {l.note && (
                          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{l.note}</div>
                        )}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', whiteSpace: 'nowrap', fontFamily: 'var(--mono)' }}>
                        {l.sla == null ? '—' : (l.sens === 'max' ? '≤ ' : '≥ ') + l.sla + ' %'}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', whiteSpace: 'nowrap', color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                        {l.bonus ? '≥ ' + l.bonus + ' %' : '—'}
                      </td>
                      <td style={{
                        padding: '9px 12px', textAlign: 'right', whiteSpace: 'nowrap',
                        fontFamily: 'var(--mono)', fontWeight: 700,
                        color: l.valeur == null ? 'var(--text3)' : st.fg,
                      }}>
                        {l.valeur == null ? '—' : l.valeur.toFixed(2).replace('.', ',') + ' %'}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                        {l.volume == null ? '—' : l.volume}
                      </td>
                      <td style={{ padding: '9px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
                          background: st.bg, color: st.fg,
                        }}>{st.libelle}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </>
  );
}

/** Mois complet le plus récent : le mois courant n'est jamais représentatif. */
function moisParDefaut() {
  const d = new Date();
  d.setDate(0);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function KPI() {
  const [activeIndicator, setActiveIndicator] = useState('grille');
  const [perimetre, setPerimetre] = useState(perimetreCourant());
  const [erreur, setErreur] = useState('');

  // Changer de périmètre change les données de tous les panneaux : on remonte
  // l'arbre avec une clé plutôt que de propager l'information dans chacun.
  const changerPerimetre = p => { definirPerimetre(p); setPerimetre(p); };

  // Les indicateurs signalent ici leurs échecs de chargement, qui restaient
  // auparavant invisibles : la page s'affichait vide sans aucun message.
  useEffect(() => {
    abonnerErreurKpi(setErreur);
    return () => abonnerErreurKpi(null);
  }, []);

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">SLA Production FTTH — Covage</div>
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>Engagements contractuels, suivis mois par mois</div>
      </div>

      <div className="page">
        {erreur && (
          <div style={{
            background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b',
            borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, fontWeight: 600,
          }}>
            {erreur}
          </div>
        )}
        {/* Périmètre — n'a pas de sens sur la grille, qui montre les deux */}
        {activeIndicator !== 'grille' && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 14, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text3)', marginRight: 4 }}>Périmètre</span>
            {[['production', 'Production'], ['vt', 'Visite technique']].map(([v, lib]) => (
              <button key={v} onClick={() => changerPerimetre(v)} style={{
                padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${perimetre === v ? '#185FA5' : 'var(--border)'}`,
                background: perimetre === v ? '#185FA5' : 'var(--bg2)',
                color: perimetre === v ? '#fff' : 'var(--text2)',
              }}>{lib}</button>
            ))}
          </div>
        )}

        {/* Onglets indicateurs */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
          {[
            { key: 'grille', label: 'Grille SLA' },
            { key: 'i001', label: 'I001 — Délai Prise de RDV' },
            { key: 'i002', label: 'I002 — Délai d\'intervention' },
            { key: 'i003', label: 'I003 — Respect E-RDV' },
            { key: 'i004', label: 'I004 — RVNH' },
            { key: 'i005', label: 'I005 — Délai émission PV/CRI' },
            { key: 'i006', label: 'I006 — Transmission devis/DOSTEC' },
            { key: 'i007', label: 'I007 — Réussite 1ère inter' },
          ].map(({ key, label, soon }) => (
            <button key={key} onClick={() => !soon && setActiveIndicator(key)} style={{
              padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              cursor: soon ? 'not-allowed' : 'pointer', border: 'none',
              background: activeIndicator === key ? '#185FA5' : 'var(--bg3)',
              color: activeIndicator === key ? '#fff' : soon ? 'var(--text3)' : 'var(--text2)',
              opacity: soon ? 0.5 : 1,
            }}>
              {label} {soon && '(à venir)'}
            </button>
          ))}
        </div>

        {activeIndicator === 'grille' && (
          <GrilleSLA onOuvrir={(src, per) => { changerPerimetre(per === 'VT' ? 'vt' : 'production'); setActiveIndicator(src); }} />
        )}
        {activeIndicator === 'i001' && <I001Panel key={perimetre} />}
        {activeIndicator === 'i002' && <I002Panel key={perimetre} />}
        {activeIndicator === 'i003' && <I003Panel key={perimetre} />}
        {activeIndicator === 'i004' && <I004Panel key={perimetre} />}
        {activeIndicator === 'i005' && <I005Panel key={perimetre} />}
        {activeIndicator === 'i006' && <I006Panel key={perimetre} />}
        {activeIndicator === 'i007' && <I007Panel key={perimetre} />}
      </div>
    </>
  );
}
